import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { StyleSheet, TextStyle, View, ViewStyle } from "react-native"

import type { LayoutChangeEvent } from "react-native"
import { router, useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { usePostHog } from "posthog-react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { EskTypeT } from "types/sponsor"

import { BrandBanner } from "src/components/BrandBanner"
import { ChatButton } from "src/components/ChatButton"
import {
  ContentLibraryGrid,
  ContentLibraryItem,
  ContentLibrarySearchBar,
  ContentLibrarySortBar,
  searchItems,
  sortAndFilterItems,
  SortKey,
  todoItemToContentLibraryItem,
} from "src/components/ContentLibrary"
import { Text } from "src/components/Ignite"
import { SidekickView } from "src/components/SidekickView"
import { useAuth } from "src/context/AuthContext"
import { useAvatarMode } from "src/context/AvatarModeContext"
import { useChannelBrand } from "src/context/ChannelBrandContext"
import { useChat } from "src/context/ChatContext"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCurrentMsk, useESKAvailable } from "src/queries/useConfig"
import { useCurrentConversation } from "src/queries/useConversation"
import { useCurrentRole } from "src/queries/useRole"
import { useToDoList } from "src/queries/useToDoList"
import { ThemedStyle } from "src/theme"
import { openWebsite } from "src/utils/openWebsite"
import { storage } from "src/utils/storage/mmkv"

/**
 * ContentLibraryScreen - A full-screen content library that uses SidekickView for consistent UX.
 *
 * Features:
 * - Avatar overlay with speech integration
 * - Talk/Type/Menu button cluster
 * - Todo list / content library display
 */
export default function ContentLibraryScreen() {
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()
  const { api, sponsorId } = useAuth()
  const { startConversation, startNewConversation, setIsSwitchingSidekick } = useChat()
  const { stopSpeech, isSpeaking } = useVisemes()
  const { suppressNextMediaOpen, getLastPosition } = useMediaContext()
  const { setMode } = useAvatarMode()
  const { data: currentConversation } = useCurrentConversation()
  const { data: eskData } = useESKAvailable()
  const { brandData } = useChannelBrand()
  const currentMsk = useCurrentMsk()
  const { data: todoItems = [], isLoading: isLoadingTodos } = useToDoList()
  const currentRole = useCurrentRole()
  const isReadOnlyRole = currentRole === "sponsor_admin" || currentRole === "sme"
  const { firstVisit } = useLocalSearchParams<{ firstVisit?: string }>()

  const posthog = usePostHog()

  useEffect(() => {
    if (firstVisit === "1") {
      posthog?.capture("auth_first_visit_cl_shown", { sponsorId: brandData?.id ?? null })
    }
  }, [firstVisit, posthog, brandData?.id])

  // On first visit, MediaAvatarContext.onMenuOpen() will have tabbed the avatar,
  // which de-registers the Rive instance and prevents the welcome speech from
  // playing. Override it to large so the avatar is visible and speech can play.
  // A deferred callback ensures this runs after onMenuOpen's useEffect.
  useEffect(() => {
    if (firstVisit !== "1") return
    const timer = setTimeout(() => setMode("large", false), 0)
    return () => clearTimeout(timer)
  }, [firstVisit, setMode])

  // Once welcome speech finishes playing, transition avatar back to tab mode.
  const welcomeSpeechStartedRef = useRef(false)
  useEffect(() => {
    if (firstVisit !== "1") return
    if (isSpeaking) {
      welcomeSpeechStartedRef.current = true
      return
    }
    if (welcomeSpeechStartedRef.current) {
      welcomeSpeechStartedRef.current = false
      setMode("tab", false)
    }
  }, [firstVisit, isSpeaking, setMode])

  // Sort/filter/search state
  const [activeSort, setActiveSort] = useState<SortKey>("latest")
  const [activeSidekick, setActiveSidekick] = useState<string | null>(null)
  const [activeTopic, setActiveTopic] = useState<string | null>(null)
  const [activeType, setActiveType] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchActive, setSearchActive] = useState(false)

  const handleSearchPress = useCallback(() => {
    setSearchActive(true)
    setActiveSort("latest")
    setActiveSidekick(null)
    setActiveTopic(null)
    setActiveType(null)
  }, [])
  const handleSearchCancel = useCallback(() => {
    setSearchQuery("")
    setSearchActive(false)
  }, [])

  // Build ESK lookup map: ESK type UUID → ESKType info
  const eskLookup = useMemo(() => {
    const lookup = new Map<string, { name: string; avatar_id: string }>()
    if (eskData) {
      for (const sponsor of eskData) {
        if (sponsor.esk_types) {
          for (const esk of sponsor.esk_types) {
            lookup.set(esk.type, { name: esk.name, avatar_id: esk.avatar_id })
          }
        }
      }
    }
    return lookup
  }, [eskData])

  // Auth thumbnail_url values are relative to {apiBase}/files (e.g. /hls/…/thumbnail.jpg).
  // Prepending the bare api.config.url would produce /hls/… — the /files/ segment must
  // be included so the resolved URL matches the expected /files/hls/… structure.
  const thumbnailBaseUrl = `${api.config.url.replace(/\/$/, "")}/files`

  // Map TodoItems to shared ContentLibraryItem format
  const allItems = useMemo<ContentLibraryItem[]>(() => {
    const mskName = currentMsk?.name ?? ""
    const mskAvatarId = currentMsk?.avatar_id ?? "00"
    return todoItems.map((item) => {
      const isDeliveredByPsk = !item.select_esk || item.select_esk === "PSK"
      if (isDeliveredByPsk) {
        return todoItemToContentLibraryItem(item, mskName, mskAvatarId, thumbnailBaseUrl)
      }
      const eskInfo = eskLookup.get(item.select_esk)
      return todoItemToContentLibraryItem(
        item,
        eskInfo?.name ?? mskName,
        eskInfo?.avatar_id ?? mskAvatarId,
        thumbnailBaseUrl,
      )
    })
  }, [todoItems, currentMsk, eskLookup, thumbnailBaseUrl])

  const filteredItems = useMemo(() => {
    const sorted = sortAndFilterItems(allItems, activeSort, activeSidekick, activeTopic, activeType)
    return searchItems(sorted, searchQuery)
  }, [allItems, activeSort, activeSidekick, activeTopic, activeType, searchQuery])

  const handleSortChange = useCallback((sort: SortKey) => {
    setActiveSort(sort)
    setActiveSidekick(null)
    setActiveTopic(null)
    setActiveType(null)
    // Collapse search when a filter is selected
    setSearchActive(false)
    setSearchQuery("")
  }, [])

  const handleSubFilterChange = useCallback(
    (filter: { sidekick?: string | null; topic?: string | null; type?: string | null }) => {
      if (filter.sidekick !== undefined) setActiveSidekick(filter.sidekick)
      if (filter.topic !== undefined) setActiveTopic(filter.topic)
      if (filter.type !== undefined) setActiveType(filter.type)
    },
    [],
  )

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  const [headerHeight, setHeaderHeight] = useState(60)

  const handleHeaderLayout = useCallback((e: LayoutChangeEvent) => {
    setHeaderHeight(e.nativeEvent.layout.height)
  }, [])

  const validSponsor = !!sponsorId && sponsorId !== "unknown-sponsor"
  const homeScreenKey = validSponsor ? `auth.preferredHomeScreen.${sponsorId}` : ""

  const handleGoToChat = useCallback(() => {
    if (homeScreenKey) storage.set(homeScreenKey, "chat")
    router.replace("/")
  }, [homeScreenKey])

  const handleItemSelect = useCallback(
    async (item: ContentLibraryItem) => {
      const currentTaskId = currentConversation?.active_task_id

      // If the selected item already matches the active conversation, no need to start
      // a new one — just resume where the user left off.
      if (item.taskId === currentTaskId) {
        if (item.isMediaMode && item.mediaUrl) {
          const resourceKey = item.sourceResourceId ?? item.mediaUrl
          const savedPosition = getLastPosition(resourceKey)
          const startTime = savedPosition?.offset ?? "0"

          if (item.mediaType === "video") {
            router.push({
              pathname: "/video-player",
              params: {
                videoUrl: item.mediaUrl,
                resourceId: item.sourceResourceId,
                title: item.title,
                taskId: item.taskId,
                startTime,
                ...(item.thumbnailUrl ? { thumbnailUrl: item.thumbnailUrl } : {}),
              },
            } as any)
          } else if (item.mediaType === "audio") {
            router.push({
              pathname: "/audio-player",
              params: {
                audioUrl: item.mediaUrl,
                audioTitle: item.title,
                startTime,
                autoplay: "true",
                resourceId: item.sourceResourceId,
                taskId: item.taskId,
              },
            } as any)
          } else if (item.mediaType === "youtube" && item.youtubeId) {
            router.push({
              pathname: "/youtube-player",
              params: {
                videoId: item.youtubeId,
                resourceId: item.sourceResourceId,
                startTime,
              },
            } as any)
          } else if (item.mediaType === "pdf") {
            router.push({
              pathname: "/pdf-viewer",
              params: {
                url: item.mediaUrl,
                title: item.title,
                resourceId: item.sourceResourceId,
                taskId: item.taskId,
              },
            } as any)
          } else if (item.mediaType === "image") {
            router.push({
              pathname: "/image-viewer",
              params: { imageUrl: item.mediaUrl },
            } as any)
          } else if (item.mediaType === "url") {
            openWebsite(item.mediaUrl, router.push)
          }
        } else {
          // Non-media module — show the conversation history.
          // Pass skipRedirect so index.tsx does not bounce back to the Content Library.
          router.replace({ pathname: "/", params: { skipRedirect: "1" } } as any)
        }
        return
      }

      await stopSpeech()

      // Close the active conversation via REST before starting a new one
      try {
        await api.closeCurrentConversation()
      } catch (error) {
        console.error("[ContentLibrary] Failed to close current conversation:", error)
        // Continue — startConversation will reconnect and the server will handle cleanup
      }

      const isDeliveredByPsk = !item.selectEsk || item.selectEsk === "PSK"

      if (isDeliveredByPsk) {
        startNewConversation(item.taskId!, undefined, item.isMediaMode)
      } else {
        const eskInfo = eskLookup.get(item.selectEsk!)
        if (!eskInfo) {
          console.error("[ContentLibrary] ESK info not found for:", item.selectEsk)
          return
        }
        setIsSwitchingSidekick(true)
        startConversation(
          item.sponsorId!,
          item.selectEsk as unknown as EskTypeT,
          parseInt(eskInfo.avatar_id),
          eskInfo.name,
          item.taskId!,
          false,
          undefined,
          item.isMediaMode,
        )
      }

      if (item.isMediaMode && item.mediaUrl) {
        // Launch the player immediately using the media_url from the todo item.
        // Suppress the redundant PlayVideo / OpenResource the backend fires next.
        suppressNextMediaOpen()

        const resourceKey = item.sourceResourceId ?? item.mediaUrl
        const savedPosition = getLastPosition(resourceKey)
        const startTime = savedPosition?.offset ? savedPosition.offset : "0"

        if (item.mediaType === "video") {
          router.push({
            pathname: "/video-player",
            params: {
              videoUrl: item.mediaUrl,
              resourceId: item.sourceResourceId,
              title: item.title,
              taskId: item.taskId,
              startTime,
              ...(item.thumbnailUrl ? { thumbnailUrl: item.thumbnailUrl } : {}),
            },
          } as any)
        } else if (item.mediaType === "audio") {
          router.push({
            pathname: "/audio-player",
            params: {
              audioUrl: item.mediaUrl,
              audioTitle: item.title,
              startTime,
              autoplay: "true",
              resourceId: item.sourceResourceId,
              taskId: item.taskId,
            },
          } as any)
        } else if (item.mediaType === "youtube" && item.youtubeId) {
          router.push({
            pathname: "/youtube-player",
            params: {
              videoId: item.youtubeId,
              resourceId: item.sourceResourceId,
              startTime,
            },
          } as any)
        } else if (item.mediaType === "pdf") {
          router.push({
            pathname: "/pdf-viewer",
            params: {
              url: item.mediaUrl,
              title: item.title,
              resourceId: item.sourceResourceId,
              taskId: item.taskId,
            },
          } as any)
        } else if (item.mediaType === "image") {
          router.push({
            pathname: "/image-viewer",
            params: { imageUrl: item.mediaUrl },
          } as any)
        } else if (item.mediaType === "url") {
          openWebsite(item.mediaUrl, router.push)
        }
        // Stay on the Content Library underneath the player —
        // closing the player will return the user here.
      } else {
        // Non-media-mode CM: switch to the chat history view.
        router.replace({ pathname: "/", params: { skipRedirect: "1" } } as any)
      }
    },
    [
      api,
      currentConversation,
      startConversation,
      startNewConversation,
      setIsSwitchingSidekick,
      eskLookup,
      stopSpeech,
      suppressNextMediaOpen,
      getLastPosition,
    ],
  )

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={true}
        style={themed($container)}
        onMenuPress={() => router.push("/menu")}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
        modeToggle={<ChatButton compact onPress={handleGoToChat} />}
      >
        {/* Custom Header - positioned above the content */}
        <View
          style={[
            themed($headerContainer),
            {
              top: insets.top,
              left: insets.left,
              right: insets.right,
            },
          ]}
          pointerEvents="box-none"
          onLayout={handleHeaderLayout}
        >
          <View style={styles.headerBrandRow}>
            <View style={styles.headerBrandSpacer} />
            <View style={styles.headerBrandCenter}>
              <BrandBanner />
            </View>
            <View style={styles.headerBrandSpacer} />
          </View>
          <Text style={themed($headerTitle)}>Content Library</Text>
        </View>
        {/* Content */}
        <View
          style={[
            themed($contentWrapper),
            {
              paddingTop: headerHeight + 4,
            },
          ]}
        >
          <ContentLibrarySortBar
            items={allItems}
            activeSort={activeSort}
            activeSidekick={activeSidekick}
            activeTopic={activeTopic}
            activeType={activeType}
            onSortChange={handleSortChange}
            onSubFilterChange={handleSubFilterChange}
            onSearchPress={handleSearchPress}
            searchActive={searchActive}
          />
          {searchActive && (
            <ContentLibrarySearchBar
              query={searchQuery}
              onQueryChange={setSearchQuery}
              onCancel={handleSearchCancel}
            />
          )}
          <ContentLibraryGrid
            items={filteredItems}
            isLoading={isLoadingTodos}
            onSelectItem={isReadOnlyRole ? undefined : handleItemSelect}
            selectionDisabled={isReadOnlyRole}
            style={styles.grid}
            contentPaddingBottom={insets.bottom + 140}
          />
        </View>
      </SidekickView>
    </View>
  )
}

const styles = StyleSheet.create({
  grid: {
    flex: 1,
  },
  headerBrandRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  headerBrandSpacer: {
    width: 45,
  },
  headerBrandCenter: {
    flex: 1,
    alignItems: "center",
  },
})

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  position: "absolute",
  // Keep below the MediaPlayerShell foreground layer (zIndex: 1000) so the
  // avatar tab/overlay renders above this header. Only needs to be above
  // the FlatList scroll content (no explicit zIndex).
  zIndex: 100,
  flexDirection: "column",
  paddingBottom: 4,
  backgroundColor: colors.background,
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 28,
  fontFamily: typography.primary.bold,
  color: colors.text,
  paddingHorizontal: 16,
  paddingTop: 8,
  paddingBottom: 4,
})

const $contentWrapper: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})
